<template>
  <v-form
    method="POST"
    action="/login"
  >
    <csrf-token />
    <div class="d-flex flex-row justify-center mx-2">
      <div />
      <div>
        <v-text-field
          name="username"
          :label="t('global.username')"
          required
          autofocus
        />
        <password-input name="password" />

        <v-switch
          class="mb-7 mt-n2 text-label"
          name="remember"
          color="primary"
        >
          <template #label>
            <span class="text-font">
              {{ t('login.remember_me') }}
            </span>
          </template>
        </v-switch>

        <v-btn
          type="submit"
          color="secondary"
          class="mb-3"
          block
          :text="t('login.title')"
        />

        <v-divider
          v-if="passkeySupported"
          class="my-3"
        />

        <v-btn
          v-if="passkeySupported"
          variant="outlined"
          color="primary"
          class="mb-3"
          block
          prepend-icon="fas fa-key"
          :loading="passkeyLoading"
          :text="t('login.sign_in_with_passkey')"
          @click="loginWithPasskey"
        />

        <v-alert
          v-if="passkeyError"
          type="error"
          density="compact"
          class="mb-3"
          :text="passkeyError"
          closable
          @click:close="passkeyError = ''"
        />

        <v-btn
          class="text-center w-100"
          variant="plain"
          color="primary"
          to="/password/request"
          :text="t('password.forgotten')"
        />
        <v-btn
          class="text-center w-100"
          variant="plain"
          color="primary"
          to="/username/recovery"
          :text="t('login.forgotten_username')"
        />
        <cookie-consent class="mt-8" />
      </div>
    </div>
    <div />
    <div class="d-flex justify-end">
      <language-switcher
        icon
        @update="handlePreferredLanguageUpdate"
      />
    </div>
    <input
      v-if="preferredLanguage"
      type="hidden"
      name="preferred_language"
      :value="preferredLanguage"
    >
  </v-form>
</template>

<script setup lang="ts">
import { startAuthentication } from '@simplewebauthn/browser';
import axios from 'axios';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CsrfToken from '@/components/CsrfToken.vue';
import CookieConsent from './CookieConsent.vue';
import LanguageSwitcher from './LanguageSwitcher.vue';
import PasswordInput from './PasswordInput.vue';

const { t } = useI18n();
const preferredLanguage = ref<string | undefined>();
const passkeyLoading = ref(false);
const passkeyError = ref('');
const passkeySupported = ref(
  typeof window !== 'undefined' && !!window.PublicKeyCredential,
);

function handlePreferredLanguageUpdate(language: string) {
  preferredLanguage.value = language;
}

async function loginWithPasskey(): Promise<void> {
  passkeyError.value = '';
  passkeyLoading.value = true;
  try {
    const { data: options } = await axios.post('/passkeys/authentication-options');
    const authentication = await startAuthentication({ optionsJSON: options });
    await axios.post('/passkeys/authenticate', {
      start_authentication_response: JSON.stringify(authentication),
    });
    window.location.href = '/';
  }
  catch (e: any) {
    if (e?.name === 'NotAllowedError') {
      return;
    }
    passkeyError.value = t('login.passkey_error');
  }
  finally {
    passkeyLoading.value = false;
  }
}
</script>
